import * as React from "react";

import { cn } from "../../lib/utils";
import type { ScheduleKind } from "../../lib/scheduleTypeKind";

export type { ScheduleKind };

export interface ScheduleTypeBadgeProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "children"
> {
  kind: ScheduleKind;
  /** Short uppercase label shown inside the pill. Defaults to `kind.toUpperCase()`. */
  label?: string;
  /** Renders the badge as an anchor (used for Lab deep-links). */
  href?: string;
  /** Optional title on the anchor when `href` is set. */
  title?: string;
}

/**
 * Static class maps — Tailwind's JIT can only see arbitrary-value classes
 * when they appear as literal strings in source. Never interpolate
 * `kind` into a class name at runtime.
 */
const KIND_CLASS: Record<ScheduleKind, string> = {
  prod: "bg-[hsl(var(--badge-prod)/0.12)] text-[hsl(var(--badge-prod))] border-[hsl(var(--badge-prod)/0.35)]",
  demo: "bg-[hsl(var(--badge-demo)/0.15)] text-[hsl(var(--badge-demo))] border-[hsl(var(--badge-demo)/0.40)]",
  lab: "bg-[hsl(var(--badge-lab)/0.12)] text-[hsl(var(--badge-lab))] border-[hsl(var(--badge-lab)/0.35)]",
};

const KIND_HOVER: Record<ScheduleKind, string> = {
  prod: "hover:bg-[hsl(var(--badge-prod)/0.2)]",
  demo: "hover:bg-[hsl(var(--badge-demo)/0.22)]",
  lab: "hover:bg-[hsl(var(--badge-lab)/0.2)]",
};

/**
 * Schedule-type pill (prod / demo / lab) styled from the
 * `--badge-prod` / `--badge-demo` / `--badge-lab` tokens in
 * `packages/ui/themes/dashboard.css`.
 *
 * Spec: wiki/guides/ux/design-system.md schedule/status badge guidance.
 * The design system consolidated production / demo / lab schedules onto a
 * single section hue (`--sch-schedule`, emerald); type is carried by this
 * small uppercase pill, never by a separate section accent.
 *
 * Pair this React component with `renderScheduleTypeBadgeHtml` from
 * `@appcaire/ui/lib/scheduleTypeBadgeHtml` for Bryntum HTML-string cell
 * renderers where React is not available.
 */
export const ScheduleTypeBadge = React.forwardRef<
  HTMLElement,
  ScheduleTypeBadgeProps
>(({ kind, label, href, title, className, ...rest }, ref) => {
  const content = label ?? kind.toUpperCase();
  const base =
    "inline-flex items-center rounded-full border px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] no-underline";

  if (href) {
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        data-testid="schedule-type-badge"
        data-kind={kind}
        href={href}
        title={title}
        className={cn(
          base,
          KIND_CLASS[kind],
          "cursor-pointer",
          KIND_HOVER[kind],
          className,
        )}
        {...rest}
      >
        {content}
      </a>
    );
  }

  return (
    <span
      ref={ref as React.Ref<HTMLSpanElement>}
      data-testid="schedule-type-badge"
      data-kind={kind}
      className={cn(base, KIND_CLASS[kind], className)}
      {...rest}
    >
      {content}
    </span>
  );
});
ScheduleTypeBadge.displayName = "ScheduleTypeBadge";
